import { useCallback, useEffect, useMemo, useState } from "react";
import { Bot, CheckCircle2, ChevronDown, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EVENT_META = [
  ["payment_review", "Payment review", "Every pending deposit / withdrawal - inline approve + reject."],
  ["payment_initiated", "Payment initiated", "A player started a deposit or gateway checkout."],
  ["payment_completed", "Payment completed", "A player's deposit was approved or confirmed by the gateway."],
  ["new_member", "New member", "Pings when a player joins your club."],
  ["big_redeem", "Big redeem", "Fires when a single redeem goes over $500."],
  ["wallet_topup_approved", "Wallet top-up approved", "Admin approved a wallet top-up for a player."],
  ["wallet_withdrawal_approved", "Wallet withdrawal approved", "Admin approved a wallet withdrawal."],
  ["game_credit_low", "Game credit low", "Any game credit drops below $50."],
  ["game_account_auto_created", "Game account auto-created", "Provider auto-created a game account for a player."],
  ["player_request", "Reset password", "Only game reset-password requests with Telegram approve / reject buttons."],
  ["referral_bonus_paid", "Referral bonus paid", "A client referral bonus was paid out."],
  ["gateway_deposit_completed", "Gateway deposit completed", "Gateway provider confirmed a player's deposit."],
  ["support_message", "Support messages", "A customer sends a new live support chat message or photo."],
  ["identity_verification", "ID verification", "Legacy ID alerts. Current ID verification is handled automatically by Didit."],
];

const emptyBot = {
  display_name: "",
  bot_token: "",
  chat_id: "",
  enabled: true,
  events: Object.fromEntries(EVENT_META.map(([id]) => [id, true])),
};

export default function AdminTelegram() {
  const [bots, setBots] = useState([]);
  const [stats, setStats] = useState({});
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newBot, setNewBot] = useState(emptyBot);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/telegram");
    setBots(data.bots || []);
    setStats(data.stats || {});
    const next = {};
    (data.bots || []).forEach((bot) => {
      next[bot.id] = {
        display_name: bot.display_name || "",
        bot_token: "",
        chat_id: bot.chat_id || "",
        enabled: bot.enabled !== false,
        events: { ...emptyBot.events, ...(bot.events || {}) },
      };
    });
    setDrafts(next);
    if (!expanded && data.bots?.[0]) setExpanded(data.bots[0].id);
  }, [expanded]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const statCards = useMemo(() => [
    ["Bots configured", stats.bots_configured ?? bots.length, "good"],
    ["Notifications live", stats.notifications_live ?? bots.filter((bot) => bot.enabled).length, "good"],
    ["Webhook", stats.webhook || "Pending", "warn"],
    ["Last sent", stats.last_sent ? new Date(stats.last_sent).toLocaleString("en-US") : "-", "good"],
  ], [bots, stats]);

  const updateDraft = (id, patch) => setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const toggleEvent = (id, eventId) => updateDraft(id, {
    events: { ...drafts[id].events, [eventId]: !drafts[id].events[eventId] },
  });

  const saveBot = async (id) => {
    setSaving(id);
    try {
      await api.put(`/admin/telegram/bots/${id}`, drafts[id]);
      toast.success("Telegram bot saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const createBot = async () => {
    setSaving("new");
    try {
      const { data } = await api.post("/admin/telegram/bots", newBot);
      toast.success("Telegram bot added");
      setAdding(false);
      setNewBot(emptyBot);
      setExpanded(data.id);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const deleteBot = async (bot) => {
    if (!window.confirm(`Delete ${bot.display_name}?`)) return;
    try {
      await api.delete(`/admin/telegram/bots/${bot.id}`);
      toast.success("Telegram bot deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const testBot = async (bot) => {
    setSaving(`test-${bot.id}`);
    try {
      await api.post(`/admin/telegram/bots/${bot.id}/test`);
      toast.success("Test message sent");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Telegram</h1>
        <p className="mt-1 text-sm text-slate-500">Add bots for admin alerts. Tokens stay encrypted on the server.</p>
      </div>

      <div className="mb-6 grid gap-3 rounded-xl border border-[#D4AF37]/30 bg-[#121214] p-4 md:grid-cols-4">
        {statCards.map(([label, value, tone]) => (
          <div key={label} className={`rounded-xl border px-4 py-3 ${tone === "warn" ? "border-amber-500/30 bg-amber-500/10 text-amber-200" : "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"}`}>
            <div className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</div>
            <div className="mt-2 truncate text-lg font-bold">{value}</div>
          </div>
        ))}
      </div>

      <section className="rounded-xl border border-[#D4AF37]/25 bg-[#121214] p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Bots</h2>
            <p className="text-sm text-slate-400">Add multiple bots. Each bot can have its own token, chat ID and events.</p>
          </div>
          <button onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 font-bold text-black hover:bg-amber-400">
            <Plus className="h-4 w-4" /> Add bot
          </button>
        </div>

        {adding && (
          <BotEditor
            title="New Telegram bot"
            bot={{ id: "new", token_mask: "" }}
            draft={newBot}
            onChange={(patch) => setNewBot((current) => ({ ...current, ...patch }))}
            onToggleEvent={(eventId) => setNewBot((current) => ({ ...current, events: { ...current.events, [eventId]: !current.events[eventId] } }))}
            onSave={createBot}
            saving={saving === "new"}
            isNew
          />
        )}

        <div className="space-y-4">
          {bots.map((bot) => (
            <div key={bot.id} className="overflow-hidden rounded-xl border border-white/10 bg-black/20">
              <button onClick={() => setExpanded(expanded === bot.id ? null : bot.id)} className="flex w-full items-center justify-between gap-4 border-b border-white/10 px-5 py-4 text-left hover:bg-white/5">
                <span className="flex items-center gap-3">
                  <Bot className="h-4 w-4 text-[#D4AF37]" />
                  <span>
                    <span className="block font-bold text-white">{bot.display_name}</span>
                    <span className="block text-xs text-slate-400">{bot.bot_username ? `@${bot.bot_username}` : "Telegram bot"} · chat {bot.chat_id}</span>
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded === bot.id ? "rotate-180" : ""}`} />
              </button>
              {expanded === bot.id && drafts[bot.id] && (
                <BotEditor
                  bot={bot}
                  draft={drafts[bot.id]}
                  onChange={(patch) => updateDraft(bot.id, patch)}
                  onToggleEvent={(eventId) => toggleEvent(bot.id, eventId)}
                  onSave={() => saveBot(bot.id)}
                  onTest={() => testBot(bot)}
                  onDelete={() => deleteBot(bot)}
                  saving={saving === bot.id}
                  testing={saving === `test-${bot.id}`}
                />
              )}
            </div>
          ))}
          {bots.length === 0 && !adding && (
            <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-slate-500">No Telegram bots configured yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function BotEditor({ title, bot, draft, onChange, onToggleEvent, onSave, onTest, onDelete, saving, testing, isNew }) {
  return (
    <div className="p-5">
      {title && <div className="mb-4 text-base font-bold">{title}</div>}
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Display name">
          <input value={draft.display_name} onChange={(e) => onChange({ display_name: e.target.value })} className={inp} placeholder="Chime review" />
        </Field>
        <div className="flex items-end justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div>
            <div className="text-sm font-bold text-white">Enable notifications</div>
            <div className="text-xs text-slate-400">Off means no Telegram pings fire from this bot.</div>
          </div>
          <Switch checked={draft.enabled} onClick={() => onChange({ enabled: !draft.enabled })} />
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <Field label="Bot token">
          {!isNew && bot.token_mask && <div className="mb-2 text-xs text-slate-400">Current: {bot.token_mask} (encrypted at rest)</div>}
          <input value={draft.bot_token} onChange={(e) => onChange({ bot_token: e.target.value })} className={inp} placeholder={isNew ? "123456789:AAEA..." : "Leave blank to keep current token"} />
        </Field>
        <Field label="Reviewer chat ID">
          <input value={draft.chat_id} onChange={(e) => onChange({ chat_id: e.target.value })} className={inp} placeholder="@yourchannel or numeric chat ID" />
          <div className="mt-1 text-xs text-slate-500">Username like @whalelopes, channel username, or numeric chat ID.</div>
        </Field>
      </div>

      <div className="mt-6">
        <div className="mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Events</div>
          <p className="text-xs text-slate-400">Pick which events ping this bot's chat.</p>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {EVENT_META.map(([id, label, desc]) => (
            <button key={id} type="button" onClick={() => onToggleEvent(id)} className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:bg-white/[0.06]">
              <Switch checked={!!draft.events[id]} />
              <span>
                <span className="block font-bold text-white">{label}</span>
                <span className="block text-xs leading-relaxed text-slate-400">{desc}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {bot.last_error && (
        <div className="mt-4 rounded-lg border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">{bot.last_error}</div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 font-bold text-black hover:bg-amber-400 disabled:opacity-60">
          <CheckCircle2 className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
        </button>
        {!isNew && (
          <button onClick={onTest} disabled={testing} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 font-bold text-slate-200 hover:bg-white/5 disabled:opacity-60">
            <Send className="h-4 w-4" /> {testing ? "Sending..." : "Send test message"}
          </button>
        )}
        {!isNew && (
          <button onClick={onDelete} className="ml-auto inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-bold text-red-300 hover:bg-red-500/10">
            <Trash2 className="h-4 w-4" /> Delete bot
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function Switch({ checked, onClick }) {
  return (
    <span onClick={onClick} className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors ${checked ? "bg-amber-400" : "bg-white/10"}`}>
      <span className={`h-5 w-5 rounded-full bg-black transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </span>
  );
}

const inp = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-slate-100 outline-none focus:border-[#D4AF37]";
